import React from "react";
import { LegalPage } from "@/components/legal/LegalPage";
import { ENV } from "@/config/env";

const LAST_UPDATED = "June 1, 2026";

const SECTIONS = [
  {
    id: "overview",
    title: "1. Overview",
    body: [
      `This Privacy Policy explains how ${ENV.APP_NAME} ("WhooLive", "we", "us" or "our") collects, uses, shares and protects your personal data when you use the WhooLive website and the WhooLive official mobile applications for iOS and Android (together, the "Service").`,
      "By creating an account or otherwise using the Service, you acknowledge that you have read and understood this Privacy Policy. If you do not agree with it, please do not use the Service. This Privacy Policy should be read together with our Terms of Use.",
    ],
  },
  {
    id: "registration",
    title: "2. Account Registration & Authentication",
    body: [
      "To use most features of WhooLive you must create an account. During registration and each time you sign in we process the credentials you provide (email address and password) to create your account, authenticate you and keep your session secure. The same account can be used across the website and the official mobile apps.",
      "Passwords are stored in hashed form and are never accessible to us in plain text. We use authentication tokens to keep you signed in. We may also process security-related information (such as failed login attempts) to protect your account against unauthorised access.",
    ],
  },
  {
    id: "email",
    title: "3. Email Address & Profile Data",
    body: [
      "We use your email address to identify your account, to send you essential service communications (for example account verification, security alerts, password resets and important changes to the Service), and — only where permitted — to send you optional updates about events and streams.",
      "Your profile may include a display name, a profile picture, a short biography and other information you choose to add. This information is used to personalise your experience and, where you make it public, to identify you to other users (for example as the host of an event or the author of a chat message). You can edit or remove this information at any time, and opt out of non-essential email communications while still receiving transactional and security emails.",
    ],
  },
  {
    id: "location",
    title: "4. Geolocation",
    body: [
      "WhooLive is a location-based service. With your explicit permission, we access your device's approximate or precise location to show you live streams and events near you, to place events on the map and to sort content by distance.",
      "Location access is always requested through your device's or browser's native permission prompt and is only used while you interact with the relevant feature. You can grant or revoke location permission at any time through your device or browser settings. If you deny location access, WhooLive still works but will fall back to non-location-based content (for example the most watched live streams).",
    ],
  },
  {
    id: "camera-mic",
    title: "5. Camera & Microphone",
    body: [
      "If you choose to broadcast a live stream or capture photos or videos, we request access to your device camera and, for audio, your microphone. These are used solely to capture the media, video and sound that you choose to record or stream.",
      "We do not access your camera or microphone in the background and never activate them without your action. The microphone and camera are only used while you are actively streaming or recording. You can revoke these permissions at any time in your device settings.",
    ],
  },
  {
    id: "media",
    title: "6. Photos, Videos & Uploaded Content",
    body: [
      "You may upload or capture photos, videos and other content to use as event covers, profile pictures or stream content. We process and store this media to provide the Service, to display it within the website and mobile apps, and, where you choose, to share it publicly with other users.",
      "You are responsible for the content you upload and must have the right to share it. You can delete media you have uploaded, subject to reasonable technical and legal retention limits.",
    ],
  },
  {
    id: "livestreaming",
    title: "7. Live Streaming",
    body: [
      "When you start or watch a live stream, we process the audio and video of the broadcast, viewer counts, connection quality data and related metadata to deliver the stream in real time. Live video and audio are transmitted through our streaming infrastructure to viewers.",
      "Anyone with access to a public stream may see and hear its content. Streams may be transiently processed to enable delivery; unless a replay is explicitly saved, live content is not retained after the broadcast ends.",
    ],
  },
  {
    id: "chat",
    title: "8. Chat & Messages",
    body: [
      "Events and live streams may include a chat feature. When you send a message, we process its content, your display name and the time it was sent so that it can be shown to other participants.",
      "Please do not share sensitive personal information in chat. Messages sent in public events and streams are visible to other participants. We may process chat content to enforce our community guidelines and to keep the Service safe.",
    ],
  },
  {
    id: "notifications",
    title: "9. Notifications",
    body: [
      "With your permission, we send push notifications to inform you about activity relevant to you — for example when a followed host goes live, when an event you are interested in is starting, or about important account and safety information.",
      "To deliver notifications we process a device push token. You can turn notifications on or off at any time through your device settings.",
    ],
  },
  {
    id: "technical",
    title: "10. Technical Data, Logs & Security",
    body: [
      "To operate, secure and improve the Service we automatically collect certain technical information, such as device type, operating system, app version, IP address, language settings, crash reports, diagnostic data and usage logs (for example which screens are viewed and which features are used).",
      "This data is used for security, fraud prevention, troubleshooting, analytics and improving the reliability and performance of the Service. We use appropriate technical and organisational measures — including encryption in transit, hashed passwords and access controls — to protect your personal data. However, no method of transmission or storage is completely secure, and we cannot guarantee absolute security.",
    ],
  },
  {
    id: "legal-basis",
    title: "11. Legal Bases for Processing",
    body: [
      "Where the EU General Data Protection Regulation (GDPR) applies, we process your personal data on the following legal bases: performance of a contract (to provide the Service you request), your consent (for example for location, camera, microphone and notifications), our legitimate interests (to secure and improve the Service), and compliance with legal obligations.",
      "Where we rely on consent, you can withdraw it at any time without affecting the lawfulness of processing carried out before withdrawal.",
    ],
  },
  {
    id: "providers",
    title: "12. Technical Service Providers & Data Sharing",
    body: [
      "We do not sell your personal data. To operate WhooLive we rely on trusted third-party technical providers that process data on our behalf under appropriate confidentiality and data-protection obligations. These include, depending on the feature you use: cloud hosting and database providers; live-streaming infrastructure providers (such as LiveKit) used to transmit audio and video; map and location providers; analytics and crash-reporting providers; and push-notification delivery providers.",
      "We also share data with other users where you choose to make content public, and with public authorities where required by law or to protect the rights, safety and property of WhooLive, our users or the public.",
    ],
  },
  {
    id: "retention",
    title: "13. Data Retention",
    body: [
      "We keep your personal data only for as long as necessary to provide the Service and for the purposes described in this policy, or as required by law. When your account is deleted, we delete or anonymise your personal data, except where we must retain certain information for legal, security, dispute-resolution or accounting reasons.",
    ],
  },
  {
    id: "deletion",
    title: "14. How to Delete Your Account & Data",
    body: [
      "You can request deletion of your account and associated personal data at any time. You may do this directly from your account settings within the website or the mobile app where the option is available, or by contacting the Data Controller using the details at the end of this policy with the email address associated with your account.",
      "Once we verify your request, we will delete or anonymise your personal data within the timeframe required by applicable law, except for information we are legally required or permitted to retain. Deleting your account is permanent and may result in the loss of your content, profile and history.",
    ],
  },
  {
    id: "children",
    title: "15. Children & Age Requirement",
    body: [
      "WhooLive is intended for adults. The minimum age to create an account or use the Service is 18 years, because the Service includes live streaming and camera, microphone and user-generated content features. Where local law expressly permits use by minors below this age with the consent of a parent or legal guardian, such use is only allowed where that verifiable consent has been provided and where required by applicable law.",
      "During registration we require your date of birth and ask you to confirm that you meet the minimum age; sign-ups that do not meet the requirement are blocked. We do not knowingly collect personal data from anyone who does not meet the applicable age requirement.",
      "If we become aware that an account does not comply with these age requirements — or that it was created without any legally required parental or guardian consent — we may suspend or permanently delete the account and delete the associated data. If you believe an underage or non-compliant account is using the Service, please contact us so we can take action.",
    ],
  },
  {
    id: "rights",
    title: "16. Your Rights",
    body: [
      "Depending on your location, you may have the right to access, correct, update or delete your personal data; to object to or restrict certain processing; to data portability; and to withdraw consent at any time. You can exercise many of these rights directly in the website or app (for example by editing your profile or deleting your account).",
      "To exercise any right, or if you have concerns about how we handle your data, please contact the Data Controller using the details below. You also have the right to lodge a complaint with your local data protection authority.",
    ],
  },
  {
    id: "changes",
    title: "17. Changes to This Policy",
    body: [
      "We may update this Privacy Policy from time to time. When we make material changes, we will update the \"Last updated\" date above and, where appropriate, notify you within the Service. Your continued use of the Service after changes take effect means you accept the updated policy.",
    ],
  },
  {
    id: "controller",
    title: "18. Data Controller & Contact",
    body: [
      "The data controller responsible for the processing of your personal data is Mario Mignacca (Data Controller — WhooLive).",
      "If you have any questions about this Privacy Policy, wish to request deletion of your account, or wish to exercise your data protection rights, please contact the Data Controller. We will respond to your request within the timeframe required by applicable law.",
    ],
  },
];

export default function Privacy() {
  return (
    <LegalPage
      testidPrefix="privacy"
      badgeLabel="Legal"
      title="Privacy Policy"
      lastUpdated={LAST_UPDATED}
      intro={`Your privacy matters to us. This policy describes what personal data ${ENV.APP_NAME} collects, why we collect it, and the choices and rights you have.`}
      sections={SECTIONS}
      crossLink={{
        to: "/terms",
        label: "Read our Terms of Use",
        desc: "By using WhooLive you also agree to our Terms of Use, which govern acceptable use of the Service and user-generated content.",
      }}
    />
  );
}
